/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useContext, useState } from 'react';
import { Pagination, PaginationItem, PaginationLink, Form, FormGroup, Input, InputGroup, InputGroupAddon, Collapse, Badge, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, Table, TabPane, TabContent, Nav, NavItem, NavLink } from 'reactstrap';
import Alert from 'react-s-alert';
import CKEditor from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';

// import services
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

const FAQs = props => {

    // variables
    let timeout = 0;

    // states
    const [isLoading, setIsLoading] = useState(true);
    const [isLoading2, setIsLoading2] = useState(false);
    const [isLoading3, setIsLoading3] = useState(false);
    const [faqs, setFAQs] = useState({ client: [], counsellor: [] });
    const [activeTab, setActiveTab] = useState("client");
    const [modal, setModal] = useState({ isVisible: false, isUpdate: false });
    const [modalData, setModalData] = useState({ id: "", type: "", question: "", answer: "", orderNo: "" });
    const [confirmationModal, setConfirmationModal] = useState({ isVisible: false, id: "" });
    const [pagination, setPagination] = useState({ limit: 10, page: 1 });

    // contexts
    const { theme, colors } = useContext(ThemeContext);

    // use Effects
    // will be called when value for pagination changes
    useEffect(() => {
        setIsLoading(true);
        getFAQs();
    }, [pagination.page, pagination.limit, pagination.searchVal]);

    // call getFAQs api and get data
    const getFAQs = () => {
        AdminService.getFAQs(pagination.limit, pagination.page, pagination.searchVal).then(data => {
            try {
                // set data to state "FAQs"
                setFAQs(data.faqs);
                // check if current page number is not greater than total pages
                if (data.pagination.totalPages <= data.pagination.page) {
                    data.pagination.page = data.pagination.totalPages;
                }
                // set pagination values
                setPagination(data.pagination);
                setIsLoading(false);
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Admin FAQs useEffect', 'Error in try catch block', err.stack);
                setIsLoading(false);
            }
        });
    }

    // to toggle FAQ div
    // args: type for determine current tab, index for div to toggle
    const toggle = (type, index) => {
        try {
            // create a copy of array
            const tempArray = [...faqs[type]];
            // modify array
            tempArray[index].isOpen = !tempArray[index].isOpen;
            // save array
            setFAQs({ ...faqs, [type]: tempArray });
        } catch (err) {
            // show error message if error
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            // add error to log
            GeneralService.logError('at Admin FAQs toggle', 'Error in try catch block', err.stack);
        }
    };

    //utility function
    // create html_markup
    const createMarkup = (html) => {
        return { __html: html };
    }

    // handle functions
    // handle value change for inputs
    const handleChange = e => {
        try {
            if (modal.isVisible)
                setModalData({ ...modalData, [e.target.name]: e.target.value });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin FAQs handleChange', 'Error in try catch block', err.stack);
        }
    }

    // function to add faq
    const handleQuestionSave = e => {
        try {
            setIsLoading3(true);
            e.preventDefault();
            AdminService.postFAQ(modalData.orderNo, modalData.type, modalData.question, modalData.answer).then(data => {
                Alert.info("FAQ added successfully", {
                    position: 'top-right',
                    effect: 'slide'
                });
                getFAQs();
                setModal({ isVisible: false, isUpdate: false });
                setModalData({ id: "", type: "", question: "", answer: "", orderNo: "" });
                setIsLoading3(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading3(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading3(false);
            GeneralService.logError('at Admin FAQ handleQuestionSave', 'Error in try catch block', err.stack);
        }
    }

    // function to update faq
    const handleQuestionUpdate = e => {
        try {
            setIsLoading3(true);
            e.preventDefault();
            AdminService.putFAQ(modalData.id, modalData.orderNo, modalData.type, modalData.question, modalData.answer).then(data => {
                Alert.info("FAQ updated successfully", {
                    position: 'top-right',
                    effect: 'slide'
                });
                getFAQs();
                setModal({ isVisible: false, isUpdate: false });
                setModalData({ id: "", type: "", question: "", answer: "", orderNo: "" });
                setIsLoading3(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading3(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading3(false);
            GeneralService.logError('at Admin FAQ handleQuestionUpdate', 'Error in try catch block', err.stack);
        }
    }

    // function to delete faq
    const handleQuestionDelete = (id) => {
        try {
            setIsLoading2(true);
            AdminService.deleteFAQ(id).then(data => {
                Alert.info("FAQ deleted successfully", {
                    position: 'top-right',
                    effect: 'slide'
                });
                getFAQs();
                setConfirmationModal({ isVisible: false, id: "" });
                setModal({ isVisible: false, isUpdate: false });
                setModalData({ id: "", type: "", question: "", answer: "", orderNo: "" });
                setIsLoading2(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin FAQ handleQuestionDelete', 'Error in try catch block', err.stack);
            setIsLoading2(false)
        }
    }

    return (
        <main>
            <div className="container-fluid">
                <div className="d-flex my-4">
                    <Button className="ml-auto" style={{ backgroundColor: "#384E73" }} onClick={() => { setModal({ isVisible: true, isUpdate: false }); setModalData({ id: "", orderNo: faqs[activeTab].length + 1, type: activeTab, answer: "", question: "" }) }}>Add</Button>
                </div>
                {
                    isLoading ?
                        <h3 className="text-center mt-5">Loading...</h3> :
                        faqs[activeTab].length === 0 ?
                            <h3 className="text-center mt-5">No questions found<br />Add new questions by clicking on Add button</h3> :
                            <div>
                                <Nav justified tabs className='mt-4 mx-3'>
                                    <NavItem style={{ fontSize: "1.2rem", fontWeight: "600", cursor: "pointer" }}>
                                        <NavLink
                                            style={{ borderWidth: "medium" }}
                                            active={activeTab === "client"}
                                            onClick={() => setActiveTab("client")}
                                        >
                                            For Clients
                                        </NavLink>
                                    </NavItem>
                                    <NavItem style={{ fontSize: "1.2rem", fontWeight: "600", cursor: "pointer" }}>
                                        <NavLink
                                            style={{ borderWidth: "medium" }}
                                            active={activeTab === "counsellor"}
                                            onClick={() => setActiveTab("counsellor")}
                                        >
                                            For Counsellors
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="client">
                                        {
                                            faqs.client.map((item, index) => (
                                                <div key={index} className="p-3 my-3" style={theme.card}>
                                                    <div className="row px-3">
                                                        <Badge style={{ backgroundColor: colors.primary, marginRight: "15px" }}>{item.orderNo}</Badge>
                                                        <span style={theme.headingBold}>{item.question}</span>
                                                        <span className="ml-auto">
                                                            <span style={{ cursor: "pointer" }} onClick={() => { setModal({ isVisible: true, isUpdate: true }); setModalData({ id: item._id, orderNo: item.orderNo, type: item.type, question: item.question, answer: item.answer }) }}><i className="fas fa-edit"></i></span>
                                                            <span style={{ cursor: "pointer", marginLeft: "15px" }} onClick={() => toggle("client", index)}>
                                                                {
                                                                    faqs.client[index].isOpen
                                                                        ? <i className="fas fa-angle-up"></i>
                                                                        : <i className="fas fa-angle-down"></i>
                                                                }
                                                            </span>
                                                        </span>
                                                    </div>

                                                    <Collapse isOpen={faqs.client[index].isOpen}>
                                                        <hr />
                                                        <div className="my-auto" style={theme.paragraph} dangerouslySetInnerHTML={createMarkup(item.answer)}></div>
                                                    </Collapse>
                                                </div>
                                            ))
                                        }
                                    </TabPane>
                                    <TabPane tabId="counsellor">
                                        {
                                            faqs.counsellor.map((item, index) => (
                                                <div key={index} className="p-3 my-3" style={theme.card}>
                                                    <div className="row px-3">
                                                        <Badge style={{ backgroundColor: colors.primary, marginRight: "15px" }}>{item.orderNo}</Badge>
                                                        <span style={theme.headingBold}>{item.question}</span>
                                                        <span className="ml-auto">
                                                            <span style={{ cursor: "pointer" }} onClick={() => { setModal({ isVisible: true, isUpdate: true }); setModalData({ id: item._id, orderNo: item.orderNo, type: item.type, question: item.question, answer: item.answer }); }}><i className="fas fa-edit"></i></span>
                                                            <span style={{ cursor: "pointer", marginLeft: "15px" }} onClick={() => toggle("counsellor", index)}>
                                                                {
                                                                    faqs.counsellor[index].isOpen
                                                                        ? <i className="fas fa-angle-up"></i>
                                                                        : <i className="fas fa-angle-down"></i>
                                                                }
                                                            </span>
                                                        </span>
                                                    </div>

                                                    <Collapse isOpen={faqs.counsellor[index].isOpen}>
                                                        <hr />
                                                        <div className="my-auto" style={theme.paragraph} dangerouslySetInnerHTML={createMarkup(item.answer)}></div>
                                                    </Collapse>
                                                </div>
                                            ))
                                        }
                                    </TabPane>
                                </TabContent>
                            </div>
                }
            </div>
            <Modal size="sm" isOpen={confirmationModal.isVisible} backdrop={true}>
                <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setConfirmationModal({ isVisible: false, id: "" })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Are you sure?</span></ModalHeader>
                <ModalBody>
                    <Label>By clicking on Yes button question will be <b>Deleted</b></Label>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handleQuestionDelete(confirmationModal.id)} disabled={isLoading2}>
                        {isLoading2 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Yes
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal size="lg" isOpen={modal.isVisible} backdrop={true}>
                <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => { setModal({ isVisible: false, isUpdate: false }); setModalData({ id: "", type: "", orderNo: 0, question: "", answer: "" }); }}><i className="fas fa-times"></i></span>}>
                    <span style={theme.headingBold}>{modal.isUpdate ? "Update question" : "Add question"}</span>
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={modal.isUpdate ? handleQuestionUpdate : handleQuestionSave}>
                        <FormGroup>
                            <Label for="orderNo" style={theme.formBodyText} >Order no</Label>
                            <Input type="number" min={1} max={faqs[activeTab].length + 1} name="orderNo" value={modalData.orderNo} onChange={handleChange} className="form-control" style={theme.formInput} required />
                        </FormGroup>

                        <FormGroup>
                            <Label for="type" style={theme.formBodyText} >Type</Label>
                            <Input type="select" name="type" value={modalData.type} onChange={handleChange} className="form-control" style={theme.formInput} required >
                                <option value="">Select</option>
                                <option value="client">Client</option>
                                <option value="counsellor">Counsellor</option>
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label for="question" style={theme.formBodyText} >Question</Label>
                            <Input type="text" name="question" value={modalData.question} onChange={handleChange} className="form-control" style={theme.formInput} required />
                        </FormGroup>

                        <FormGroup>
                            <Label for="answer" style={theme.formBodyText} >Answer</Label>
                            <CKEditor
                                editor={ClassicEditor}
                                data={modalData.answer}
                                config={{
                                    placeholder: "You can write here with different styles",
                                    toolbar: ['heading', '|', 'bold', 'underLine', 'italic', 'bulletedList', 'numberedList', 'blockQuote', '|', 'indent', 'outdent', '|', 'undo', 'redo']
                                }}
                                onInit={editor => {
                                    // setEditor(editor);
                                    editor.editing.view.change(writer => {
                                        writer.setStyle("height", "22rem", editor.editing.view.document.getRoot());
                                    });
                                }}
                                onChange={(event, editor) => {
                                    const data = editor.getData();
                                    handleChange({ target: { name: "answer", value: data } });
                                }}
                            />
                        </FormGroup>

                        <div className="d-flex">
                            <Button type="submit" style={theme.formButton} disabled={isLoading3}>{isLoading3 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Submit</Button>
                            {modal.isUpdate ? <Button type="button" className="ml-auto" style={{ backgroundColor: "#9F1D1D" }} onClick={() => setConfirmationModal({ isVisible: true, id: modalData.id })}><span><i className="far fa-trash-alt"></i></span></Button> : null}
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </main>
    )

}

export default FAQs;